import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Select, Button, DatePicker, Space, Typography, Tag, Modal, InputNumber, message, Statistic, Row, Col, Input, Popconfirm } from 'antd';
import moment from 'moment';
import { getClients, getSoaEntries, addSoaEntry, updateSoaEntry, deleteSoaEntry, recordSoaPayment, downloadSoaIndependent } from '../api';
import { DownloadOutlined, FilterOutlined, DollarOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const StatementOfAccount = () => {
    const [form] = Form.useForm();
    const [entryForm] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [soaData, setSoaData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const res = await getClients();
                if (res.success) {
                    setClients(res.data);
                }
            } catch (err) {
                console.error('Failed to fetch clients', err);
            }
        };
        fetchClients();
    }, []);

    const handleSearch = async (values) => {
        setLoading(true);
        try {
            const params = {
                client_name: values.client_name,
                start_date: values.dateRange?.[0]?.format('YYYY-MM-DD'),
                end_date: values.dateRange?.[1]?.format('YYYY-MM-DD'),
            };
            const res = await getSoaEntries(params);
            if (res.success) {
                setSoaData(res.data);
            }
        } catch (err) {
            message.error('Failed to fetch SOA data');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        const values = form.getFieldsValue();
        if (!values.client_name) {
            message.warning('Please select a client first');
            return;
        }
        try {
            message.loading({ content: 'Generating SOA PDF...', key: 'download' });
            const params = {
                client_name: values.client_name,
                start_date: values.dateRange?.[0]?.format('YYYY-MM-DD'),
                end_date: values.dateRange?.[1]?.format('YYYY-MM-DD'),
            };
            const blob = await downloadSoaIndependent(params);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `SOA_${values.client_name.replace(/ /g, '_')}_${moment().format('YYYYMMDD')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success({ content: 'SOA Downloaded', key: 'download' });
        } catch (err) {
            message.error({ content: 'Failed to download SOA', key: 'download' });
        }
    };

    const showModal = (record = null) => {
        setEditingEntry(record);
        if (record) {
            entryForm.setFieldsValue({
                ...record,
                date: moment(record.date),
                due_date: record.due_date ? moment(record.due_date) : null,
            });
        } else {
            entryForm.resetFields();
            entryForm.setFieldsValue({
                client_name: form.getFieldValue('client_name'),
                date: moment(),
                due_date: moment().add(30, 'days'),
            });
        }
        setModalVisible(true);
    };

    const handleModalSubmit = async (values) => {
        setSubmitting(true);
        try {
            const payload = {
                ...values,
                date: values.date.format('YYYY-MM-DD'),
                due_date: values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
            };
            let res;
            if (editingEntry) {
                res = await updateSoaEntry(editingEntry.id, payload);
                message.success('Entry updated');
            } else {
                res = await addSoaEntry(payload);
                message.success('Entry added');
            }
            if (res.success) {
                setModalVisible(false);
                handleSearch(form.getFieldsValue());
            }
        } catch (err) {
            message.error('Failed to save entry');
        } finally {
            setSubmitting(false);
        }
    };

    const showPaymentModal = (record) => {
        setSelectedEntry(record);
        paymentForm.setFieldsValue({ amount: Number(record.balance || 0) });
        setPaymentModalVisible(true);
    };

    const handlePaymentSubmit = async (values) => {
        setSubmitting(true);
        try {
            const res = await recordSoaPayment(selectedEntry.id, values.amount);
            if (res.success) {
                message.success('Payment recorded');
                setPaymentModalVisible(false);
                handleSearch(form.getFieldsValue());
            }
        } catch (err) {
            message.error('Failed to record payment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await deleteSoaEntry(id);
            if (res.success) {
                message.success('Entry deleted');
                handleSearch(form.getFieldsValue());
            }
        } catch (err) {
            message.error('Failed to delete');
        }
    };

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => moment(date).format('DD-MMM-YYYY'),
            sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
        },
        { title: 'Ref No.', dataIndex: 'ref_no', key: 'ref_no' },
        { title: 'LPO #', dataIndex: 'lpo_no', key: 'lpo_no' },
        {
            title: 'Opening Amount',
            dataIndex: 'total_amount',
            key: 'total_amount',
            align: 'right',
            render: (val) => val.toLocaleString(undefined, { minimumFractionDigits: 2 }),
        },
        {
            title: 'Pending Amount',
            dataIndex: 'balance',
            key: 'balance',
            align: 'right',
            className: 'font-weight-bold',
            render: (val) => (
                <Text strong style={{ color: val > 0 ? '#f5222d' : '#52c41a' }}>
                    {val.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Text>
            ),
        },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            key: 'due_date',
            render: (date) => date ? moment(date).format('DD-MMM-YYYY') : '-',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const isOverdue = record.balance > 0 && record.due_date && moment(record.due_date).isBefore(moment());
                if (record.balance <= 0) return <Tag color="green">PAID</Tag>;
                if (isOverdue) return <Tag color="error">OVERDUE</Tag>;
                return <Tag color="processing">PENDING</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="primary" size="small" icon={<DollarOutlined />} onClick={() => showPaymentModal(record)} disabled={record.balance <= 0}>Pay</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => showModal(record)} />
                    <Popconfirm title="Delete this entry?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    const totalOpening = Array.isArray(soaData) ? soaData.reduce((sum, item) => sum + Number(item.total_amount), 0) : 0;
    const totalPending = Array.isArray(soaData) ? soaData.reduce((sum, item) => sum + Number(item.balance), 0) : 0;

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Statement of Account (Independent Ledger)</Title>

            <Card style={{ marginBottom: 24 }}>
                <Form form={form} layout="inline" onFinish={handleSearch}>
                    <Form.Item label="Client" name="client_name" rules={[{ required: true, message: 'Select Client' }]} style={{ minWidth: 250 }}>
                        <Select showSearch placeholder="Select Client" options={Array.isArray(clients) ? clients.map(c => ({ label: c.name, value: c.name })) : []} />
                    </Form.Item>
                    <Form.Item label="Date Range" name="dateRange"><RangePicker /></Form.Item>
                    <Form.Item><Button type="primary" icon={<FilterOutlined />} htmlType="submit" loading={loading}>Search</Button></Form.Item>
                    <Form.Item><Button icon={<DownloadOutlined />} onClick={handleDownload} disabled={!Array.isArray(soaData) || soaData.length === 0}>Download PDF</Button></Form.Item>
                    <Form.Item><Button type="primary" ghost icon={<PlusOutlined />} onClick={() => showModal()}>Add Entry</Button></Form.Item>
                </Form>
            </Card>

            {Array.isArray(soaData) && soaData.length > 0 && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={8}><Card><Statistic title="Total Billed" value={totalOpening} precision={2} suffix="AED" /></Card></Col>
                    <Col span={8}><Card><Statistic title="Total Pending" value={totalPending} precision={2} suffix="AED" valueStyle={{ color: totalPending > 0 ? '#cf1322' : '#3f8600' }} /></Card></Col>
                    <Col span={8}><Card><Statistic title="Collection %" value={totalOpening > 0 ? ((totalOpening - totalPending) / totalOpening) * 100 : 0} precision={1} suffix="%" /></Card></Col>
                </Row>
            )}

            <Card bodyStyle={{ padding: 0 }}>
                <Table columns={columns} dataSource={Array.isArray(soaData) ? soaData : []} rowKey="id" loading={loading} pagination={false}
                    summary={(pageData) => {
                        let totalBilled = 0;
                        let totalUnpaid = 0;
                        if (Array.isArray(pageData)) {
                            pageData.forEach(({ total_amount, balance }) => {
                                totalBilled += Number(total_amount || 0);
                                totalUnpaid += Number(balance || 0);
                            });
                        }
                        return (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={3}><b>Totals</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right"><Text strong>{totalBilled.toLocaleString(undefined, { minimumFractionDigits: 2 })} Dr</Text></Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} align="right"><Text strong type="danger">{totalUnpaid.toLocaleString(undefined, { minimumFractionDigits: 2 })} Dr</Text></Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} colSpan={3} />
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
            </Card>

            <Modal title={editingEntry ? "Edit Entry" : "Add SOA Entry"} open={modalVisible} onCancel={() => setModalVisible(false)} onOk={() => entryForm.submit()} confirmLoading={submitting}>
                <Form form={entryForm} layout="vertical" onFinish={handleModalSubmit}>
                    <Form.Item label="Client" name="client_name" rules={[{ required: true }]}><Input readOnly /></Form.Item>
                    <Row gutter={16}>
                        <Col span={12}><Form.Item label="Date" name="date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                        <Col span={12}><Form.Item label="Due Date" name="due_date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}><Form.Item label="Ref No." name="ref_no" rules={[{ required: true }]}><Input placeholder="Invoice No" /></Form.Item></Col>
                        <Col span={12}><Form.Item label="LPO #" name="lpo_no"><Input placeholder="LPO Number" /></Form.Item></Col>
                    </Row>
                    <Form.Item label="Total Amount" name="total_amount" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} precision={2} /></Form.Item>
                </Form>
            </Modal>

            <Modal title="Record Payment" open={paymentModalVisible} onCancel={() => setPaymentModalVisible(false)} onOk={() => paymentForm.submit()} confirmLoading={submitting}>
                <Form form={paymentForm} layout="vertical" onFinish={handlePaymentSubmit}>
                    <p>Recording payment for: <b>{selectedEntry?.ref_no}</b></p>
                    <p>Remaining Balance: <b>AED {Number(selectedEntry?.balance || 0).toFixed(2)}</b></p>
                    <Form.Item label="Amount" name="amount" rules={[{ required: true, message: 'Enter amount' }, { type: 'number', min: 0.01 }]}>
                        <InputNumber style={{ width: '100%' }} precision={2} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default StatementOfAccount;
